import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import { Box, Container, Typography, IconButton, TextField, CircularProgress, Stack, Skeleton } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import GetCookieByName from "../../hooks/GetCookie";
import {
  usePlanSummary,
  usePlanWhatsappConversations,
  usePlanWhatsappThread,
  useSendPlanWhatsappMessage,
  useMarkPlanWhatsappRead,
  useSetPlanWhatsappAiPaused,
} from "../../hooks/usePlanSummary";
import {
  formatRelativeTime,
  groupMessagesByDate,
  getNumberColor,
  MessageBubble,
  AiStatusChip,
  AiProgressBar,
  ChatListTabs,
  ChatSearchField,
  sortConversationsNewestFirst,
  setConversationUnread,
  UnreadBadge,
  withLiveMessage,
  hasRealName,
  conversationDisplayName,
  formatWaId,
  upsertThreadMessage,
  upsertThreadMessageStatus,
} from "../whatsapp/chatUiHelpers";

// The "View WhatsApp Msg" destination — every WhatsApp conversation belonging to an
// influencer on THIS plan, in the same conversation-list + thread layout as the admin-wide
// WhatsApp Inbox (WhatsAppInboxPage.jsx), but backed by the plan-scoped, org-member-gated
// endpoints (see usePlanSummary.js) instead of the admin-only ones — any org member with
// access to this plan can open it, not just admins.

// Unlike upsertThreadMessage/upsertThreadMessageStatus (shared via chatUiHelpers.jsx as-is),
// this one stays local rather than sharing WhatsAppInboxPage.jsx's bumpInboxPreview — that
// version also returns a `found` flag so the Inbox can force a refetch when a brand-new
// conversation isn't in ANY currently-cached page (it does setQueriesData across every
// page/filter combo). This screen has exactly one, unpaginated conversations query per plan,
// so there's no "which page" ambiguity to resolve and the simpler shape is correct here.
function bumpConversationPreview(oldData, conversationId, message) {
  if (!oldData?.conversations) return oldData;
  const idx = oldData.conversations.findIndex((c) => c.conversationId === conversationId);
  if (idx === -1) return oldData;
  // The page marks it read straight away when the chat is the one currently open.
  const updated = withLiveMessage(oldData.conversations[idx], message);
  const rest = oldData.conversations.filter((_, i) => i !== idx);
  return { ...oldData, conversations: [updated, ...rest] };
}

// The "All" / "Unread" tabs. A chat you open from the Unread tab stays listed (until you leave the
// tab or reload) instead of vanishing from under your cursor the moment it is marked read, the way
// WhatsApp's own Unread filter behaves.
export function filterConversationsByTab(conversations, tab, openedIds) {
  if (tab !== "unread") return conversations;
  return conversations.filter((c) => c.unread || (openedIds && openedIds.has(c.conversationId)));
}

// Search by name, phone number (digits only, so "+91 98116-29081" works) or Instagram username. The plan's
// whole chat list is loaded on this page, so this filters in the browser.
export function filterConversationsBySearch(conversations, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return conversations;
  const digits = q.replace(/\D/g, "");
  return conversations.filter((c) => {
    const names = [c.customerName, c.influencerDbId?.userName, c.influencerDbId?.username];
    if (names.some((n) => n && String(n).toLowerCase().includes(q))) return true;
    return digits.length >= 3 && String(c.customerWaId || "").includes(digits);
  });
}

function ConversationRow({ conv, isSelected, onSelect }) {
  const color = getNumberColor(conv.ourPhoneNumberId || conv.ourDisplayPhoneNumber);
  const lastMsg = (conv.messages || [])[0];
  const unread = Boolean(conv.unread);
  const unreadMessages = conv.unreadMessages ?? (unread ? 1 : 0);
  const preview = lastMsg ? `${lastMsg.isFromUs ? "You: " : ""}${lastMsg.text || `[${lastMsg.type || "attachment"}]`}` : "No messages yet";

  return (
    <Box
      onClick={() => onSelect(conv.conversationId)}
      sx={{
        px: 2,
        py: 1.5,
        cursor: "pointer",
        borderLeft: isSelected ? "3px solid #25D366" : "3px solid transparent",
        bgcolor: isSelected ? "rgba(37,211,102,0.06)" : "transparent",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: isSelected ? "rgba(37,211,102,0.06)" : "rgba(0,0,0,0.02)" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: "Quicksand",
            fontWeight: unread ? 800 : 700,
            fontSize: 13.5,
            color: "#1C1C2E",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {conversationDisplayName(conv)}
        </Typography>
        <AiStatusChip status={conv.qa?.status} paused={conv.qa?.paused} />
        {conv.optedOut && <BlockRoundedIcon sx={{ fontSize: 13, color: "#E34C14", flexShrink: 0 }} />}
        <Typography sx={{ fontSize: 10.5, color: unread ? "#25D366" : "#9CA3AF", fontWeight: unread ? 700 : 400, flexShrink: 0 }}>
          {formatRelativeTime(conv.lastMessageAt)}
        </Typography>
      </Stack>
      {hasRealName(conv) && (
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#9CA3AF", pl: 2.25, mt: 0.1 }}>
          {formatWaId(conv.customerWaId)}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 2.25, mt: 0.4 }}>
        <Typography
          sx={{
            fontFamily: "Quicksand",
            fontSize: 12,
            color: unread ? "#374151" : "#9CA3AF",
            fontWeight: unread ? 600 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {preview}
        </Typography>
        <UnreadBadge count={unreadMessages} />
      </Stack>
    </Box>
  );
}

function ThreadComposer({ conversation, onSend, sending }) {
  const [text, setText] = useState("");
  const accentColor = getNumberColor(conversation?.ourPhoneNumberId || conversation?.ourDisplayPhoneNumber);
  const windowClosed = conversation?.messagingWindow === "closed";
  const optedOut = conversation?.optedOut;
  const disabled = windowClosed || optedOut;

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled || sending) return;
    setText("");
    onSend(trimmed);
  };

  return (
    <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(255,255,255,0.9)", flexShrink: 0 }}>
      {disabled && (
        <Typography sx={{ px: 2.5, py: 1.5, fontSize: 12, fontFamily: "Quicksand", color: "#9CA3AF", fontStyle: "italic" }}>
          {optedOut
            ? "This person has opted out — sending is disabled to respect that."
            : "This creator hasn't messaged in the last 24 hours — WhatsApp only allows a pre-approved template message until they message again."}
        </Typography>
      )}
      {!disabled && (
        <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "18px", fontFamily: "Quicksand", fontSize: 13.5, background: "rgba(0,0,0,0.02)" } }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!text.trim() || sending}
            sx={{
              bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)",
              color: text.trim() ? "#fff" : "#9CA3AF",
              "&:hover": { bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)", opacity: 0.9 },
              width: 38,
              height: 38,
            }}
          >
            {sending ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}

function ThreadPanel({ planId, conversationId, onSend, sending }) {
  const { data, isLoading } = usePlanWhatsappThread(planId, conversationId);
  const { enqueueSnackbar } = useSnackbar();
  const setAiPausedMutation = useSetPlanWhatsappAiPaused(planId);
  const scrollRef = useRef(null);
  const conversation = data?.conversation;
  const accentColor = getNumberColor(conversation?.ourPhoneNumberId || conversation?.ourDisplayPhoneNumber);
  const messages = conversation?.messages || [];
  const grouped = groupMessagesByDate(messages);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversationId, messages.length]);

  if (!conversationId) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13.5 }}>
          Select a conversation to view it
        </Typography>
      </Box>
    );
  }

  if (isLoading || !conversation) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5, p: 2.5 }}>
        {[...Array(6)].map((_, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: i % 2 === 0 ? "flex-end" : "flex-start" }}>
            <Skeleton variant="rounded" width={`${40 + (i % 3) * 15}%`} height={44} sx={{ borderRadius: i % 2 === 0 ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "rgba(255,255,255,0.85)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: accentColor, boxShadow: `0 0 0 3px ${accentColor}30` }} />
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 14, color: "#1C1C2E" }}>
              {conversationDisplayName(conversation)}
            </Typography>
            {hasRealName(conversation) && (
              <Typography sx={{ fontFamily: "Quicksand", fontSize: 11.5, color: "#9CA3AF" }}>
                {formatWaId(conversation.customerWaId)}
              </Typography>
            )}
          </Stack>
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 10.5, color: accentColor, fontWeight: 700 }}>
            via {conversation.ourDisplayPhoneNumber}
          </Typography>
        </Box>
        {conversation.optedOut && (
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#E34C14", ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
            <BlockRoundedIcon sx={{ fontSize: 13 }} /> Opted out
          </Typography>
        )}
      </Box>

      <AiProgressBar
        progress={conversation.aiProgress}
        pausing={setAiPausedMutation.isLoading}
        onTogglePause={(paused) =>
          setAiPausedMutation.mutate(
            { conversationId, paused },
            { onError: (error) => enqueueSnackbar(error?.response?.data?.message || "Could not change AI pause", { variant: "error" }) }
          )
        }
      />

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          background: "linear-gradient(145deg, rgba(224,247,232,0.4) 0%, rgba(237,241,255,0.3) 100%)",
        }}
      >
        {Object.entries(grouped).map(([dateLabel, msgs]) => (
          <Box key={dateLabel}>
            <Box sx={{ display: "flex", alignItems: "center", my: 2, gap: 1.5 }}>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: "Quicksand", color: "#9CA3AF", px: 1.5, py: 0.4, bgcolor: "rgba(0,0,0,0.04)", borderRadius: 99, letterSpacing: 0.5 }}>
                {dateLabel}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
            </Box>
            {msgs.map((msg, i) => (
              <MessageBubble key={msg.waMessageId || `msg-${dateLabel}-${i}`} message={msg} accentColor={accentColor} />
            ))}
          </Box>
        ))}
      </Box>

      <ThreadComposer conversation={conversation} onSend={onSend} sending={sending} />
    </Box>
  );
}

function PlanWhatsAppPage() {
  const { id: planId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const socketRef = useRef(null);
  const joinedRoomsRef = useRef(new Set());
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  // Chats opened this visit that were unread — kept on the Unread tab, see filterConversationsByTab.
  const [openedIds, setOpenedIds] = useState(() => new Set());
  // The socket handlers below live for the whole visit and can't see fresh state, so they read these.
  // `explicit` is true only once the user has clicked a chat: the page auto-opens the newest chat on
  // load, and that must not silently mark it read before anyone has looked at it.
  const selectedIdRef = useRef(null);
  const explicitSelectRef = useRef(false);
  const markReadRef = useRef(() => {});

  const { data: planData } = usePlanSummary(planId);
  const { data, isLoading } = usePlanWhatsappConversations(planId);
  // Memoized so the effects below (auto-select, socket room join) only re-run when the
  // actual conversation list changes, not on every render's fresh `|| []` fallback reference.
  const conversations = useMemo(() => sortConversationsNewestFirst(data?.conversations || []), [data]);
  const unreadCount = useMemo(() => conversations.filter((c) => c.unread).length, [conversations]);
  const visibleConversations = useMemo(
    () => filterConversationsBySearch(filterConversationsByTab(conversations, tab, openedIds), search),
    [conversations, tab, openedIds, search]
  );
  const sendMutation = useSendPlanWhatsappMessage(planId);
  const markReadMutation = useMarkPlanWhatsappRead(planId);

  selectedIdRef.current = selectedConversationId;
  const markRead = (conversationId) => {
    queryClient.setQueryData(["plan-whatsapp-conversations", planId], (old) => setConversationUnread(old, conversationId, false));
    markReadMutation.mutate(conversationId);
  };
  markReadRef.current = markRead;

  // Only a click counts as "opened it" — see explicitSelectRef.
  const handleSelect = (conversationId) => {
    explicitSelectRef.current = true;
    setSelectedConversationId(conversationId);
    const conv = conversations.find((c) => c.conversationId === conversationId);
    if (conv?.unread) {
      setOpenedIds((prev) => new Set(prev).add(conversationId));
      markRead(conversationId);
    }
  };

  useEffect(() => {
    if (!selectedConversationId && conversations.length) {
      setSelectedConversationId(conversations[0].conversationId);
    }
  }, [conversations, selectedConversationId]);

  // Live updates — same one-socket-per-page pattern as WhatsAppInboxPage.jsx, scoped to
  // whichever conversations belong to this plan.
  useEffect(() => {
    let authToken;
    try {
      authToken = JSON.parse(GetCookieByName("auth"))["token"];
    } catch (e) {
      return undefined;
    }

    const socket = io(new URL(process.env.REACT_APP_API_URL).origin, { auth: { token: authToken } });
    socketRef.current = socket;
    joinedRoomsRef.current = new Set();

    socket.on("new-whatsapp-message", ({ conversationId, message }) => {
      queryClient.setQueryData(["plan-whatsapp-thread", planId, conversationId], (old) => upsertThreadMessage(old, conversationId, message));
      let foundInList = false;
      queryClient.setQueryData(["plan-whatsapp-conversations", planId], (old) => {
        foundInList = Boolean(old?.conversations?.some((c) => c.conversationId === conversationId));
        return bumpConversationPreview(old, conversationId, message);
      });
      // A chat that isn't in the cached list yet (first message from a new creator) needs a real fetch.
      if (!foundInList) queryClient.invalidateQueries({ queryKey: ["plan-whatsapp-conversations", planId] });
      // The creator wrote into the chat that is open on screen right now: it is being read.
      if (!message?.isFromUs && explicitSelectRef.current && selectedIdRef.current === conversationId) {
        markReadRef.current(conversationId);
      }
    });

    // A teammate opened a chat — refresh so our Unread tab matches. A read from this plan's own page
    // names the plan; a read from the main Inbox carries no plan id and can be any plan's chat.
    socket.on("whatsapp-read-update", ({ planId: updatedPlanId }) => {
      if (!updatedPlanId || String(updatedPlanId) === String(planId)) {
        queryClient.invalidateQueries({ queryKey: ["plan-whatsapp-conversations", planId] });
      }
    });

    socket.on("whatsapp-message-status", ({ conversationId, waMessageId, status }) => {
      queryClient.setQueryData(["plan-whatsapp-thread", planId, conversationId], (old) => upsertThreadMessageStatus(old, conversationId, waMessageId, status));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [planId, queryClient]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const toJoin = conversations.map((c) => c.conversationId).filter((id) => id && !joinedRoomsRef.current.has(id));
    if (!toJoin.length) return;

    const doJoin = () => {
      toJoin.forEach((id) => {
        socket.emit("join", id);
        joinedRoomsRef.current.add(id);
      });
    };
    if (socket.connected) doJoin();
    else socket.once("connect", doJoin);
  }, [conversations]);

  const handleSend = async (text) => {
    setSending(true);
    try {
      const res = await sendMutation.mutateAsync({ conversationId: selectedConversationId, text });
      if (!res?.success) throw new Error(res?.message || "Failed to send message");
      queryClient.setQueryData(["plan-whatsapp-thread", planId, selectedConversationId], (old) => upsertThreadMessage(old, selectedConversationId, res.message));
      queryClient.setQueryData(["plan-whatsapp-conversations", planId], (old) => bumpConversationPreview(old, selectedConversationId, res.message));
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error.message || "Failed to send message", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <DashboardHeaderSection hideSearch />
      <Container sx={{ marginTop: 8, pb: 6, pl: { xs: 2, sm: 3, lg: "108px" }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Same back-button treatment as the Plan screen's own header (PlanSummary.jsx) —
                a plain IconButton + ArrowBackIcon, not a text link, so this reads as the same
                navigation affordance the user already knows from the plan page itself. */}
            <IconButton onClick={() => navigate(`/plans-summary/${planId}`)} size="small" sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 1 }}>
              <WhatsAppIcon sx={{ color: "#25D366" }} /> {planData?.plan?.name ? `${planData.plan.name} — WhatsApp` : "Plan WhatsApp"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            border: "1px solid rgba(0,0,0,0.07)",
            borderRadius: "16px",
            overflow: "hidden",
            height: "calc(100vh - 260px)",
            minHeight: 520,
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: 300, md: 340 }, flexShrink: 0, borderRight: "1px solid rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
            <ChatListTabs
              tab={tab}
              onChange={(value) => {
                setTab(value);
                setOpenedIds(new Set()); // leaving the tab drops the chats kept visible after being opened
              }}
              allCount={conversations.length}
              unreadCount={unreadCount}
            />
            <ChatSearchField value={search} onChange={setSearch} />
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {isLoading ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={54} sx={{ mb: 1, borderRadius: "10px" }} />
                  ))}
                </Box>
              ) : conversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13 }}>
                    No WhatsApp conversations yet for this plan's influencers.
                  </Typography>
                </Box>
              ) : visibleConversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13 }}>
                    {search.trim() ? `No chats match "${search.trim()}".` : "No unread chats — you're all caught up."}
                  </Typography>
                </Box>
              ) : (
                visibleConversations.map((conv) => (
                  <ConversationRow
                    key={conv.conversationId}
                    conv={conv}
                    isSelected={conv.conversationId === selectedConversationId}
                    onSelect={handleSelect}
                  />
                ))
              )}
            </Box>
          </Box>

          <ThreadPanel planId={planId} conversationId={selectedConversationId} onSend={handleSend} sending={sending} />
        </Box>
      </Container>
    </Box>
  );
}

export default PlanWhatsAppPage;
